import { StatusPill, Tooltip } from 'component-library';
import { LatestSoftwareVersion, SystemDetail } from '../../../../models/ApiModels';
import useWindowSize from '../../../../utils/UseWindowSize';

interface Props {
  system: SystemDetail;
  unitType: string;
  latestSoftwareVersion?: LatestSoftwareVersion;
}

const SystemDetailsSoftwareVersion = ({ system, unitType, latestSoftwareVersion }: Props) => {
  const windowSize = useWindowSize();

  function compareVersions(current?: string, latest?: string): 'beyond' | 'update' | 'latest' {
    if (!current || !latest) return 'update';
    const currentParts = current.split('.').map(Number);
    const latestParts = latest.split('.').map(Number);

    for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
      const currentPart = currentParts[i] || 0;
      const latestPart = latestParts[i] || 0;

      if (currentPart > latestPart) {
        return 'beyond';
      } else if (currentPart < latestPart) {
        return 'update';
      }
    }
    return 'latest';
  }

  function getStatusPill(current?: string, latest?: string) {
    const versionStatus = compareVersions(current, latest);
    switch (versionStatus) {
      case 'beyond':
        return { color: 'Light Blue' as any, text: 'Beyond' };
      case 'latest':
        return { color: 'Green' as any, text: 'Latest' };
      case 'update':
      default:
        return { color: 'Red' as any, text: 'Update' };
    }
  }

  function tooltipText() {
    switch (compareVersions(currentVersion, latestVersion)) {
      case 'beyond':
        return 'You are special! This unit has special software on it, maybe a beta release. Enjoy the new features, and reach out if you have feedback!';
      case 'latest':
        return 'This unit has the latest and greatest software version, yet!';
      case 'update':
      default:
        return 'Software update needed! This unit does not have the latest software update. Download and update to the latest version.';
    }
  }

  let currentVersion: string | undefined;
  let latestVersion: string | undefined;

  switch (unitType) {
    case 'LCU':
      currentVersion = system.lightningHardwareSystem?.controlUnit?.firmwareVersion;
      latestVersion = latestSoftwareVersion?.lcuVersion;
      break;
    case 'LCPPrimary':
      currentVersion = system.lightningHardwareSystem?.primaryControlPanel?.firmwareVersion;
      latestVersion = latestSoftwareVersion?.lcpVersion;
      break;
    case 'LCPSecondary':
      currentVersion = system.lightningHardwareSystem?.secondaryControlPanel?.firmwareVersion;
      latestVersion = latestSoftwareVersion?.lcpVersion;
      break;
    case 'LSUPort':
      currentVersion = system.lightningHardwareSystem?.portServo?.firmwareVersion;
      latestVersion = latestSoftwareVersion?.lsuVersion;
      break;
    case 'LSUStarboard':
      currentVersion = system.lightningHardwareSystem?.starboardServo?.firmwareVersion;
      latestVersion = latestSoftwareVersion?.lsuVersion;
      break;
    default:
      currentVersion = undefined;
      latestVersion = undefined;
  }

  return (
    <span className='flex gap-2'>
      <StatusPill
        color={getStatusPill(currentVersion, latestVersion).color}
        text={getStatusPill(currentVersion, latestVersion).text}
      />
      <Tooltip
        position={windowSize?.width >= 1280 ? 'left' : 'right'}
        heading={'Latest version: ' + (latestVersion || 'n/a')}
        description={tooltipText()}
      />
    </span>
  );
};

export default SystemDetailsSoftwareVersion;
